// u.jic.io FACE Registry 客户端:短名字 ↔ 活体人脸模板 ↔ 0x 收款地址。
// 匹配在服务端完成(上传 128 维描述子,服务端算距离),模板全库不下发。
const JIC_URL = "https://u.jic.io";

export type FaceIdentity = {
  type: "jic" | "address" | "x" | "email" | "phone";
  value: string;
  method: string;
  verifiedAt: string;
};

export type ServerFaceRecord = {
  address: string;
  name: string;
  method: string;
  enrolledAt: string;
  updatedAt: string;
  identities?: FaceIdentity[];
};

export type ServerFaceMatch = ServerFaceRecord & {
  distance: number;
  confidence: number;
  mode?: "personal" | "relay" | "prereg";
  slug?: string;
  label?: string;
  note?: string | null;
  claimedBy?: string | null;
  viaAnchor?: string;
};

export async function bindFaceOnServer(
  descriptor: number[],
  jwt: string,
  method = "face-mesh-challenge-v0"
): Promise<{ ok: boolean; record?: ServerFaceRecord; error?: string; boundTo?: string }> {
  const response = await fetch(`${JIC_URL}/v1/face/bind`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({ descriptor, method }),
  });
  return (await response.json()) as { ok: boolean; record?: ServerFaceRecord; error?: string; boundTo?: string };
}

export async function resolveFaceOnServer(
  descriptor: number[]
): Promise<{ match: ServerFaceMatch | null; count: number } | null> {
  try {
    const response = await fetch(`${JIC_URL}/v1/face/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ descriptor }),
    });
    const data = (await response.json()) as { ok?: boolean; match?: ServerFaceMatch | null; count?: number };
    if (!data.ok) return null;
    return { match: data.match || null, count: data.count || 0 };
  } catch {
    return null; // 离线或服务端不可用:调用方回退本机 Registry
  }
}

export async function fetchFaceStatus(
  nameOrAddress: string
): Promise<{ bound: boolean; public?: boolean; avatar?: string | null; record?: ServerFaceRecord } | null> {
  try {
    const response = await fetch(`${JIC_URL}/v1/face/status/${encodeURIComponent(nameOrAddress)}`);
    return (await response.json()) as { bound: boolean; public?: boolean; avatar?: string | null; record?: ServerFaceRecord };
  } catch {
    return null;
  }
}

// 锚点墙:本人自愿公开的真人只给 @名字 + 上墙时间,其余只计数
// avatar:本人自愿上传的公开头像(没有就不给)
export type FaceWall = { people: { name: string; since: string; avatar?: string }[]; bound: number; hidden: number };

export async function fetchFaceWall(): Promise<FaceWall | null> {
  try {
    const response = await fetch(`${JIC_URL}/v1/face/wall`);
    return (await response.json()) as FaceWall;
  } catch {
    return null;
  }
}

export async function setFacePublicOnServer(
  on: boolean,
  jwt: string
): Promise<{ ok: boolean; public?: boolean; error?: string }> {
  const response = await fetch(`${JIC_URL}/v1/face/public`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({ on }),
  });
  return (await response.json()) as { ok: boolean; public?: boolean; error?: string };
}

export async function fetchFaceStats(): Promise<number | null> {
  try {
    const response = await fetch(`${JIC_URL}/v1/face/stats`);
    const data = (await response.json()) as { count?: number };
    return typeof data.count === "number" ? data.count : null;
  } catch {
    return null;
  }
}

export async function resolveHandleAddress(name: string): Promise<string | null> {
  try {
    const response = await fetch(`${JIC_URL}/v1/handle/resolve/${encodeURIComponent(name)}`);
    const data = (await response.json()) as { ok?: boolean; address?: string };
    return data.ok && data.address ? data.address.toLowerCase() : null;
  } catch {
    return null;
  }
}
